import {createTheme} from '@mui/material/styles';
import {ruRU} from '@mui/x-data-grid';
import {ruRU as coreRuRU} from '@mui/material/locale';

declare module '@mui/material/styles' {
    interface Palette {
        custom: any;
    }

    interface PaletteOptions {
        custom?: any;
    }
}

const baseStyles = createTheme({
    palette: {
        primary: {
            main: '#0063FF', // Основной акцент
            dark: '#004FCC', // Hover
            contrastText: '#fff', // Цвет текста на кнопке
        },
        error: {
            main: '#ff003c',
            dark: '#D32F2F',
            contrastText: '#fff',
        },
        success: {
            main: '#05A763',
            dark: '#2E7D32',
            contrastText: '#fff',
        },
        text: {
            primary: '#33415B',
            secondary: '#8F96A6',
            disabled: '#B8BDC9',
        },
        background: {
            default: '#F5F7FF',
            paper: '#FFFFFF',
        },
        custom: {
            border: '#D1DAE3',
            borderHover: '#B8C7D9',
            borderFocused: '#B8C7D9',
        },
    },
    typography: {
        fontFamily: 'Roboto, sans-serif',
    },
});

const legacyStyles = {
    input: {
        '& input["number"]::-webkit-clear-button, & input["number"]::-webkit-outer-spin-button, & input["number"]::-webkit-inner-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0,
            display: 'none',
        },

        '& input': {
            '-moz-appearance': 'textfield',
            '-webkit-appearance': 'textfield',
            appearance: 'textfield',
        },
    },
    'input["number"]': {
        '-moz-appearance': 'textfield',
        fontSize: '50px',
    },
};

const theme = createTheme(
    baseStyles,
    {
        components: {
            // Кнопки
            MuiButton: {
                defaultProps: {
                    variant: 'contained',
                },
                styleOverrides: {
                    root: {
                        borderRadius: '50px',
                        textTransform: 'none',
                    },
                    contained: {
                        backgroundColor: baseStyles.palette.primary.main,
                        '&:hover': {
                            backgroundColor: baseStyles.palette.primary.dark,
                        },
                    },
                    outlined: {
                        borderColor: baseStyles.palette.primary.main,
                        '&:hover': {
                            borderColor: baseStyles.palette.primary.dark,
                        },
                    },
                    sizeSmall: {
                        fontSize: '12px',
                        padding: '6px 12px',
                    },
                    sizeMedium: {
                        fontSize: '16px',
                        padding: '8px 16px',
                    },
                    sizeLarge: {
                        fontSize: '24px',
                        padding: '14px 28px',
                        textTransform: 'uppercase',
                    },
                },
            },
            // Диалоги
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        borderRadius: '16px',
                    },
                },
            },
            MuiDialogTitle: {
                styleOverrides: {
                    root: {
                        color: baseStyles.palette.text.primary,
                        fontSize: '16px',
                        fontWeight: 600,
                    },
                },
            },
            MuiIconButton: {
                styleOverrides: {
                    root: {
                        '&.dialog-close': {
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            color: baseStyles.palette.text.primary,
                        },
                        '&.dialog-close svg': {
                            fill: '#000',
                        },
                    },
                },
            },
            // Текстовые инпуты в форме
            MuiTextField: {
                defaultProps: {
                    variant: 'outlined',
                },
                styleOverrides: {
                    root: {
                        '& .MuiOutlinedInput-root': {
                            height: '52px',
                            paddingRight: '14px',
                        },
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        backgroundColor: baseStyles.palette.background.paper,

                        // Базовый стиль
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: baseStyles.palette.custom.border,
                        },

                        // Hover
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: baseStyles.palette.custom.borderHover,
                        },

                        // Focus
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: baseStyles.palette.custom.borderFocused,
                            borderWidth: 1,
                        },

                        // Focus + Hover
                        '&.Mui-focused:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: baseStyles.palette.custom.borderFocused,
                        },

                        // Error
                        '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                            borderColor: baseStyles.palette.error.main,
                        },

                        // Error + Focus
                        '&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: baseStyles.palette.error.main,
                        },

                        // Условное "валидное" состояние — через кастомный класс
                        '&.Mui-validated .MuiOutlinedInput-notchedOutline': {
                            borderColor: baseStyles.palette.success.main,
                        },

                        '&.Mui-validated.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: baseStyles.palette.success.main,
                        },
                    },
                    input: {
                        padding: '15px',
                    },
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        fontSize: '16px',
                        fontWeight: 400,
                        color: baseStyles.palette.text.secondary,

                        // Focus
                        '&.Mui-focused': {
                            color: baseStyles.palette.text.secondary,
                        },

                        // Hover
                        '.MuiOutlinedInput-root:hover &': {
                            color: baseStyles.palette.text.secondary,
                        },

                        // Error
                        '&.Mui-error': {
                            color: baseStyles.palette.error.main,
                        },
                        '&.Mui-error.Mui-focused': {
                            color: baseStyles.palette.error.main,
                        },

                        // Validated
                        '.Mui-validated &': {
                            color: baseStyles.palette.success.main,
                        },
                        '.Mui-validated.Mui-focused &': {
                            color: baseStyles.palette.success.main,
                        },
                    },
                },
            },
            MuiAutocomplete: {
                styleOverrides: {
                    paper: {
                        marginTop: '4px',
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                        '& .MuiAutocomplete-listbox': {
                            maxHeight: 140,
                            overflowY: 'auto',
                        },
                    },
                    popper: {
                        modifiers: [
                            {
                                name: 'flip',
                                enabled: false,
                            },
                        ],
                    },
                },
            },
            MuiFormHelperText: {
                styleOverrides: {
                    root: {
                        fontSize: '14px',
                        color: baseStyles.palette.text.secondary,
                        '&.Mui-error': {
                            color: baseStyles.palette.error.main,
                        },
                    },
                },
            },
        },
    },
    legacyStyles,
    ruRU,
    coreRuRU,
);

export default theme;
